// Core domain types for memorybox boards.

export interface BoardConfig {
  title: string;
  tagline: string;
  subtitle: string;
  introLine: string; // may contain {freezeDate}
  /** The "how to play" line — slam-book framing for easy starts. */
  slamLine: string;
  composeButton: string;
  freezeNotice: string;
  freezeAt: string; // ISO timestamp; server value wins when remote
  maxChars: number;
  /** Host's choice: how many notes each person may pin. */
  maxNotesPerPerson: number;
  /** Host's choice: how many stickers each person may add. */
  maxStickersPerPerson: number;
  supabaseUrl: string;
  supabaseAnonKey: string;
  /** Cloudflare Worker API base — set for boxes served by the CF backend.
   *  When present, the guest app uses CloudflareStore instead of Supabase. */
  apiUrl?: string;
}

/** A board row as stored in the `boards` table (multi-box). */
export interface Board {
  id: string;
  slug: string;
  created_at: string;
  created_by: string | null;
  title: string;
  tagline: string;
  subtitle: string;
  intro_line: string;
  slam_line: string;
  freeze_at: string;
  frozen_early: boolean;
  room_word?: string | null;
  max_notes_per_person: number;
  max_stickers_per_person: number;
  theme?: Record<string, string> | null;
}

/** Build the guest-app config from a board row. */
export function configFromBoard(
  b: Board,
  supabaseUrl: string,
  supabaseAnonKey: string,
  apiUrl?: string
): BoardConfig {
  return {
    title: b.title,
    tagline: b.tagline,
    subtitle: b.subtitle,
    introLine: b.intro_line,
    slamLine: b.slam_line,
    composeButton: "leave a note",
    freezeNotice: "this board is now frozen - thank you for being there 🤍",
    freezeAt: b.freeze_at,
    maxChars: 280,
    maxNotesPerPerson: b.max_notes_per_person,
    maxStickersPerPerson: b.max_stickers_per_person,
    supabaseUrl,
    supabaseAnonKey,
    apiUrl,
  };
}

export type NoteKind = "note" | "sticker";

export interface Note {
  id: string;
  created_at: string;
  /** "note" (default) or "sticker" — a tiny decorative note. */
  kind?: NoteKind;
  name: string | null;
  message: string;
  paper: string;
  font: string;
  ink: string;
  rotation: number;
  pin: string;
  /** World coordinates on the infinite board; null = never placed/dragged, derive from spiral. */
  pos_x: number | null;
  pos_y: number | null;
  photo_path?: string | null;
  audio_path?: string | null;
  photo_url?: string | null;
  audio_url?: string | null;
  /** moderation flag (studio only). */
  hidden?: boolean;
  /** which board this note belongs to; null = legacy Old Friend board. */
  board_id?: string | null;
}

/** Fields the owner may change after pinning. */
export interface NoteUpdate {
  message?: string;
  name?: string; // '' clears the signature
  paper?: string;
  font?: string;
  ink?: string;
  pin?: string;
  pos_x?: number;
  pos_y?: number;
}

/** A note this browser created: id + the secret that proves ownership. */
export interface Mine {
  id: string;
  token: string | null;
  kind?: NoteKind;
}

/** Curated sticker set — tenderness + music + a Chennai evening.
 *  "om:XXXX" entries are hand-drawn OpenMoji SVGs (CC BY-SA 4.0,
 *  bundled in assets/stickers/); the rest are plain emoji. */
export const STICKERS: ReadonlyArray<string> = [
  "om:1F90D", "om:2728", "om:1F338", "om:1FAB7", "om:1F319", "om:1F3B6",
  "om:1F3B9", "om:1F3A4", "om:1F3A7", "om:1F54A", "om:1F98B", "om:1F48C",
  "om:1F56F", "om:1F33B", "om:1F308", "om:1F4FC",
  "om:2764", "om:1F525", "om:1F979", "om:1FAF6", "om:1F3B8", "om:1F3BB",
  "om:2615", "om:1F496",
  "om:1F98A", "om:1F430", "om:1F426", "om:1F41D", "om:1F353", "om:1F352",
  "om:1F9F8", "om:1F31F", "om:1F4AB", "om:1F337", "om:1F340", "om:1F36A",
];

/** Bundled art path for a sticker value, or null if it's plain emoji. */
export const stickerArt = (s: string): string | null =>
  s.startsWith("om:") ? `assets/stickers/${s.slice(3)}.svg` : null;

export const PAPERS = [
  { id: "cream", label: "cream" },
  { id: "kraft", label: "kraft" },
  { id: "pink", label: "soft pink" },
  { id: "sage", label: "sage" },
  { id: "mist", label: "mist blue" },
  { id: "butter", label: "butter" },
  { id: "ruled", label: "ruled" },
  { id: "torn", label: "torn edge" },
  { id: "polaroid", label: "polaroid" },
] as const;

export const FONTS = [
  { id: "caveat", label: "Caveat" },
  { id: "shadows", label: "Shadows Into Light" },
  { id: "patrick", label: "Patrick Hand" },
  { id: "homemade", label: "Homemade Apple" },
  { id: "indie", label: "Indie Flower" },
  { id: "kalam", label: "Kalam" },
  { id: "gochi", label: "Gochi Hand" },
  { id: "zeyada", label: "Zeyada" },
  { id: "dancing", label: "Dancing Script" },
  { id: "nanum", label: "Nanum Pen Script" },
  { id: "reenie", label: "Reenie Beanie" },
  { id: "kavivanar", label: "Kavivanar — தமிழ்", glyph: "அ" },
  { id: "chilanka", label: "Chilanka — മലയാളം", glyph: "അ" },
] as const;

export const INKS = [
  { id: "graphite", color: "#3a3532" },
  { id: "blue", color: "#3b4a6b" },
  { id: "sepia", color: "#6b4a35" },
  { id: "green", color: "#3d5544" },
  { id: "clay", color: "#96543a" },
] as const;

export const PIN_COLORS = ["brass", "clay", "moss", "indigo", "rose"] as const;

export const inkColor = (id: string): string =>
  INKS.find((i) => i.id === id)?.color ?? INKS[0].color;
